/**
 * Reusable modal shell for the workspace's non-preview panels.
 *
 * Renders a centered dialog over the workspace's MAIN PANE (the live preview /
 * code editor) ONLY. It is mounted INSIDE `.mol-workspace-main-pane` and its
 * backdrop is `position: absolute; inset: 0` (scoped to that pane — see
 * PanelModal.css), so the dim + `backdrop-filter` blur cover ONLY the
 * preview/editor. The chat pane and the toolbar/header are siblings outside
 * this region and stay crisp + interactive — you can click a toolbar icon to
 * switch panels while one is open (it doesn't dismiss the modal). The
 * preview/editor stay mounted (only blurred), so Monaco state and the preview
 * iframe survive. Closes on Escape or a click on the backdrop; the body scrolls
 * internally within a ~70vh cap. The panels that used to slide down between the
 * toolbar and the panes now render into this.
 *
 * Panels with view tabs (Environment scopes, Pipeline tabs, Activity views,
 * Logs sources) register them via {@link usePanelModalTabs}; the modal renders
 * them as ONE shared segmented control in the header, right-aligned between
 * the title and the close button — never a per-panel tab bar in the body.
 */

import './PanelModal.css'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import type { IconName } from '@molecule/app-icons'
import { useTranslation } from '@molecule/app-react'

import { useHistoryDismissable } from '../hooks/useHistoryDismissable.js'
import { useIsMobile } from '../hooks/useMediaQuery.js'
import { Icon } from './Icon.js'

/** One view tab in the modal header's segmented control. */
export interface PanelModalTabItem {
  /** Stable id handed back to `onSelect`. */
  id: string
  /** Localized label. */
  label: string
  /** `data-mol-id` for this tab button. */
  molId: string
}

/** Configuration a panel registers via {@link usePanelModalTabs}. */
export interface PanelModalTabsConfig {
  /** The tabs, in display order. */
  tabs: readonly PanelModalTabItem[]
  /** Id of the selected tab. */
  activeId: string
  /** Called with the clicked tab's id. */
  onSelect: (id: string) => void
  /** Accessible label for the tablist. */
  label: string
  /** `data-mol-id` for the tablist. */
  molId: string
}

/**
 * Registrar handed to panel children so they can mount their view tabs into
 * the modal header. Null when the panel renders outside a PanelModal.
 */
const PanelModalTabsContext = createContext<((config: PanelModalTabsConfig | null) => void) | null>(
  null,
)

/**
 * Mounts a panel's view tabs into the surrounding {@link PanelModal} header —
 * ONE segmented control, right-aligned next to the close button, identical
 * across every modal (Environment scopes, Pipeline tabs, Activity views, Logs
 * sources). The panel keeps its own tab state; the modal only renders it.
 *
 * Pass null to unregister (or just unmount). Outside a PanelModal this is a
 * no-op, so panels stay renderable standalone.
 * @param config - The tabs to show, or null for none.
 */
export function usePanelModalTabs(config: PanelModalTabsConfig | null): void {
  const register = useContext(PanelModalTabsContext)
  // The latest onSelect is read through a ref so an inline handler doesn't
  // re-register (and re-render the modal) every render.
  const onSelectRef = useRef(config?.onSelect)
  onSelectRef.current = config?.onSelect
  // Re-register only when the rendered substance changes.
  const key = config
    ? JSON.stringify([config.tabs, config.activeId, config.label, config.molId])
    : null
  useEffect(() => {
    if (!register) return
    if (key === null) {
      register(null)
      return
    }
    const parsed = JSON.parse(key) as [PanelModalTabItem[], string, string, string]
    register({
      tabs: parsed[0],
      activeId: parsed[1],
      label: parsed[2],
      molId: parsed[3],
      onSelect: (id) => onSelectRef.current?.(id),
    })
    return () => register(null)
  }, [register, key])
}

/** Props for {@link PanelModal}. */
export interface PanelModalProps {
  /** Whether the modal is shown. When false, nothing renders. */
  open: boolean
  /** Called when the user dismisses (Escape, backdrop click, or close button). */
  onClose: () => void
  /** Header title text. */
  title: string
  /** Optional icon-set name shown before the title. */
  icon?: IconName
  /** Stable automation id for the dialog (close button gets `${molId}-close`). */
  molId: string
  /**
   * Make the body a bounded flex column instead of a scrolling block.
   *
   * The default body scrolls itself, which is right for a panel that is a
   * document. It is wrong for one that owns its OWN viewport (Logs tails a live
   * stream and must stay pinned to the newest line): the body's height comes
   * from flex clamping, so a `height: 100%` child resolves against an
   * indefinite height and grows to its full content instead — the log
   * `<pre>` ended up 10,000px tall with nothing to scroll, so auto-scroll was a
   * no-op and "Jump to latest" could never appear. A flex column gives such a
   * child a definite height to fill.
   */
  fillBody?: boolean
  /** Panel content. */
  children: ReactNode
}

/**
 * A dismissible modal containing one workspace panel.
 * @param root0 - Component props.
 * @param root0.open - Whether the modal is visible.
 * @param root0.onClose - Dismiss handler.
 * @param root0.title - Header title.
 * @param root0.icon - Optional header icon name.
 * @param root0.molId - `data-mol-id` for the dialog.
 * @param root0.fillBody - Make the body a bounded flex column (panels that scroll internally).
 * @param root0.children - Panel content.
 * @returns The modal, or null when closed.
 */
export function PanelModal({
  open,
  onClose,
  title,
  icon,
  molId,
  fillBody,
  children,
}: PanelModalProps) {
  const { t } = useTranslation()
  const panelRef = useRef<HTMLDivElement | null>(null)
  const isMobile = useIsMobile()
  // View tabs the child panel registers via usePanelModalTabs — rendered in
  // the header, right-aligned before the close button.
  const [tabsConfig, setTabsConfig] = useState<PanelModalTabsConfig | null>(null)

  // Phones render this as a full-screen sheet — the platform back gesture must
  // close it (Android convention) rather than navigate away. Desktop dialogs
  // must not touch the history stack.
  useHistoryDismissable(open, onClose, isMobile)

  // Escape closes the topmost modal. Bound only while open.
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  // Move focus into the dialog when it opens, and back to whatever opened it when it closes.
  const openerRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    if (open) {
      if (document.activeElement instanceof HTMLElement) openerRef.current = document.activeElement
      panelRef.current?.focus()
      return
    }
    const opener = openerRef.current
    openerRef.current = null
    if (opener?.isConnected) opener.focus()
  }, [open])

  // Backdrop click closes — but only when the click is on the backdrop itself,
  // not bubbled from content inside the panel.
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose()
    },
    [onClose],
  )

  if (!open) return null

  return (
    <div
      className="mol-panel-modal-backdrop"
      onMouseDown={handleBackdropClick}
      data-mol-id={`${molId}-backdrop`}
    >
      <div
        ref={panelRef}
        className="mol-panel-modal"
        role="dialog"
        // Not aria-modal: the chat and toolbar behind a panel stay usable on purpose, and
        // aria-modal tells a screen reader that everything else is inert.
        aria-label={title}
        tabIndex={-1}
        data-mol-id={molId}
      >
        <div className="mol-panel-modal-header">
          {icon && <Icon name={icon} size={16} />}
          <span className="mol-panel-modal-title">{title}</span>
          {tabsConfig && (
            <div
              className="mol-panel-modal-tabs"
              role="tablist"
              aria-label={tabsConfig.label}
              data-mol-id={tabsConfig.molId}
              onKeyDown={(e) => {
                // Arrow keys move between tabs (roving tabindex), Home/End jump to the ends.
                if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return
                const tabs = tabsConfig.tabs
                const at = Math.max(
                  0,
                  tabs.findIndex((tab) => tab.id === tabsConfig.activeId),
                )
                const next =
                  e.key === 'Home'
                    ? 0
                    : e.key === 'End'
                      ? tabs.length - 1
                      : (at + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length
                e.preventDefault()
                tabsConfig.onSelect(tabs[next].id)
                const buttons = e.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]')
                buttons[next]?.focus()
              }}
            >
              {tabsConfig.tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  className="mol-panel-modal-tab"
                  aria-selected={tabsConfig.activeId === tab.id}
                  tabIndex={tabsConfig.activeId === tab.id ? 0 : -1}
                  data-mol-id={tab.molId}
                  onClick={() => tabsConfig.onSelect(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            className="mol-panel-modal-close"
            data-mol-id={`${molId}-close`}
            aria-label={t('workspace.panelModal.close', undefined, { defaultValue: 'Close' })}
            title={t('workspace.panelModal.close', undefined, { defaultValue: 'Close' })}
            onClick={onClose}
          >
            <Icon name="x-mark" size={16} />
          </button>
        </div>
        <div className={`mol-panel-modal-body${fillBody ? ' mol-panel-modal-body--fill' : ''}`}>
          <PanelModalTabsContext.Provider value={setTabsConfig}>
            {children}
          </PanelModalTabsContext.Provider>
        </div>
      </div>
    </div>
  )
}
